/* External */
import { schema } from 'normalizr';
import mapValues from 'lodash/mapValues';
import forEach from 'lodash/forEach';

/* Utils */
import {
  createShapeDeserializer,
  getShapeDefaultValue,
  identity,
  returnNull,
  ENTITY_DEFINED_LATER,
} from './utils';
import {
  string,
  typedef,
  shape,
  arrayOf,
  objectOf,
} from './types';

const polymorphicShape = shape({
  id: typedef({ defaultValue: null }),
  schema: string(),
});

function getPosiblePolymorphicTypeDef(possiblePolymorphicDefinition) {
  if (possiblePolymorphicDefinition instanceof schema.Entity) {
    return string();
  }

  return polymorphicShape;
}

export default function createEntity(
  entityName,
  definition = {},
  {
    preDeserialize = identity,
    posDeserialize = identity,
    processStrategy = identity,
    idAttribute,
    ...options
  } = {},
) {
  const typedefs = {};
  const normalizr = {};
  forEach(definition, (propDefinition, propName) => {
    if (propDefinition.isTypeDef) {
      typedefs[propName] = propDefinition;
    } else {
      normalizr[propName] = propDefinition;
    }
  });

  const deserializeTypedefs = createShapeDeserializer(typedefs);
  const deserializeNormalizr = (data) => mapValues(normalizr, (value, key) => data[key]);

  const Entity = new schema.Entity(entityName, normalizr, {
    processStrategy(data, ...args) {
      let newData = processStrategy(data, ...args);
      newData = preDeserialize(newData, ...args);
      newData = {
        schema: entityName,
        ...deserializeTypedefs(newData),
        ...deserializeNormalizr(newData),
      };
      return posDeserialize(newData, ...args);
    },
    idAttribute,
    ...options,
  });

  Entity.definition = {
    schema: string(entityName),
    ...typedefs,
    ...mapValues(normalizr, (value) => {
      if (value instanceof schema.Entity || value === ENTITY_DEFINED_LATER) {
        return typedef({ defaultValue: null });
      }

      if (value instanceof schema.Union) {
        return polymorphicShape;
      }

      if (Array.isArray(value)) {
        return arrayOf(getPosiblePolymorphicTypeDef(value[0]));
      }

      if (value instanceof schema.Array) {
        return arrayOf(getPosiblePolymorphicTypeDef(value.schema));
      }

      if (value instanceof schema.Values) {
        return objectOf(getPosiblePolymorphicTypeDef(value.schema));
      }

      let shapeDefinition = value;
      if (value instanceof schema.Object) {
        shapeDefinition = value.schema;
      }

      return shape(mapValues(shapeDefinition, () => typedef({ defaultValue: null })));
    }),
  };

  Entity.name = entityName;

  Entity.defaultValues = {
    schema: entityName,
    ...getShapeDefaultValue(typedefs),
    ...mapValues(normalizr, returnNull),
  };

  return Entity;
}
